import DataEPMDService from "@/services/data-service/data-epmd-service";
import { openDB } from "idb";
import UserAuthService from "@/services/user-auth-service";
import * as sjcl from "sjcl";
import { Vue } from "vue-property-decorator";
import DataCompanyService from "@/services/data-service/data-company-service";
import DataShipService from "@/services/data-service/data-ship-service";
import VmsDbSchema from "@/models/VmsDbSchema";
import { fromUnixTime, getUnixTime } from "date-fns";
import LoginService from "@/services/login-service";
import UserSessionTimeoutService from "@/services/user-session-timeout-service";
import { ActionQueueService } from "@/services/data-service/action-queue-service";
import DataPortService from "@/services/data-service/data-port-service";
import DataMscaService from "@/services/data-service/data-msca-service";
import IShip from "@/models/ship";
import IMessage from "@/models/toast-message";

export default class DataService {
  private $db = DataService.createDb();
  private dataEpmdService = new DataEPMDService(this.$db, this);
  private dataCompanies = new DataCompanyService(this.$db, this);
  private dataShips = new DataShipService(this.$db, this);
  private userAuthService = new UserAuthService();
  private loginService = new LoginService();
  private userSessionTimeoutService = new UserSessionTimeoutService(this);
  private actionQueue = new ActionQueueService(this.$db, this);
  private dataPorts = new DataPortService(this.$db, this);
  private dataMscaService = new DataMscaService(this.$db, this);
  private state = Vue.observable({
    isOnline: true,
    offlineSupport: false,
    toastMessage: [] as IMessage[],
    backendUrl: process.env.VUE_APP_BASE_URL_PREFIX ?? ""
  });

  constructor() {
    const offlineSupport = localStorage.getItem("offlineSupport");
    if (offlineSupport == null) {
      this.actions.setOfflineSupport(false);
    } else {
      this.state.offlineSupport = JSON.parse(offlineSupport);
    }
    const backendUrl = localStorage.getItem("backendUrl");
    if (backendUrl) this.state.backendUrl = backendUrl;
  }

  public getters = {
    toastMessage: () => this.state.toastMessage,
    isOnline: () => this.state.isOnline,
    isOffline: () => !this.state.isOnline,
    getBackendUrl: () => this.state.backendUrl,
    isOfflineSupport: () => this.state.offlineSupport,
    EPMD: this.dataEpmdService.getters,
    MSCA: this.dataMscaService.getters,
    Auth: {
      isUserLogged: this.userAuthService.isUserLogged.bind(this.userAuthService),
      isAdmin: this.userAuthService.isAdmin.bind(this.userAuthService)
    },
    FuncQueue: {
      funcToRun: () => [...this.actionQueue.state.funcToRun],
      isNotEmpty: () => this.actionQueue.state.funcToRun.length > 0,
      count: () => this.actionQueue.state.funcToRun.length
    }
  };

  public actions = {
    TimeOut: {
      start: this.userSessionTimeoutService.start.bind(this.userSessionTimeoutService),
      reset: this.userSessionTimeoutService.reset.bind(this.userSessionTimeoutService),
      stop: this.userSessionTimeoutService.stop.bind(this.userSessionTimeoutService)
    },
    shiftToastMessage: this.shiftToastMessage.bind(this),
    EPMD: this.dataEpmdService.actions,
    MSCA: this.dataMscaService.actions,
    Companies: this.dataCompanies.actions,
    Ships: this.dataShips.actions,
    FuncQueue: this.actionQueue.actions,
    Ports: this.dataPorts.actions,
    Auth: {
      loginUser: this.loginService.LoginUser.bind(this.loginService),
      refreshToken: this.loginService.RefreshToken.bind(this.loginService),
      getUserInfo: this.userAuthService.getUserInfo.bind(this.userAuthService),
      getShipAdmin: this.userAuthService.getShipAdmin.bind(this.userAuthService),

      removeUserInfo: this.userAuthService.removeUserInfo.bind(this.userAuthService),
      getAuthHeader: this.userAuthService.getAuthHeader.bind(this.userAuthService),
      putUserInfo: this.userAuthService.putUserInfo.bind(this.userAuthService),
      getShipRights: this.userAuthService.getShipRights.bind(this.userAuthService)
    },
    showMessage: (color: string, message: string) => {
      this.state.toastMessage.push({ color, message });
    },
    showErrorExceptionMessage: this.showErrorExceptionMessage.bind(this),
    getOfflineDate: this.getOfflineDate.bind(this),
    setOnline: () => {
      this.state.isOnline = true;
      this.actions.showMessage("success", "Network Restored!");
      return;
    },
    setOffline: async () => {
      this.state.isOnline = false;
      await this.setOfflineDate(new Date());
      return this.actions.showMessage("error", "No Network Detected!");
    },
    setOfflineSupport: (offlineSupport: boolean | null | undefined) => {
      if (!offlineSupport) offlineSupport = false;
      localStorage.setItem("offlineSupport", JSON.stringify(offlineSupport));
      this.state.offlineSupport = offlineSupport;
    },
    setBackendUrl: (url: string) => {
      localStorage.setItem("backendUrl", url);
      this.state.backendUrl = url;
    },
    reset: async () => {
      return this.clearDb();
    }
  };

  public async initialize() {
    if (!(await this.checkValidKey())) {
      await this.clearDb();
      await DataService.createDb();
    }
    const encryptCheckingKey = DataService.encryptData("ValidKey!");
    await (await this.$db).put("Valid", encryptCheckingKey, 0);
    const [ships, ...otherShips] = await this.dataShips.initialize();
    const shipIds = (ships as IShip[]).filter(x => x != undefined).map(x => x.id ?? 0);
    return Promise.all([
      this.dataEpmdService.initialize(shipIds),
      this.dataMscaService.initialize(shipIds),
      this.dataCompanies.initialize(),
      this.actionQueue.initialize(),
      this.dataPorts.initialize()
    ]);
  }

  private shiftToastMessage() {
    if (this.state.toastMessage.length === 0) return null;
    window.setTimeout(() => this.state.toastMessage.splice(0, 1), 6000);
    return this.state.toastMessage[0];
  }

  private async checkValidKey(): Promise<boolean> {
    const encryptedData = await (await this.$db).get("Valid", 0);
    if (!encryptedData) return false;
    try {
      const data = DataService.decryptData<string>(encryptedData);
      return data === "ValidKey!";
    } catch (e: any) {
      return false;
    }
  }

  private static createDb() {
    return openDB<VmsDbSchema>("vms", 6, {
      async upgrade(db, oldVersion, newVersion, transaction) {
        console.info("oldVersionDb:", oldVersion, "newVersionDb", newVersion);
        if (!db.objectStoreNames.contains("Valid")) db.createObjectStore("Valid");
        await transaction.objectStore("Valid").clear();
        if (!db.objectStoreNames.contains("EpmdHeaders")) db.createObjectStore("EpmdHeaders");
        if (!db.objectStoreNames.contains("Companies")) db.createObjectStore("Companies");
        if (!db.objectStoreNames.contains("EpmdAverages")) db.createObjectStore("EpmdAverages");
        if (!db.objectStoreNames.contains("Ships")) db.createObjectStore("Ships");
        if (!db.objectStoreNames.contains("PoolDrainOptions"))
          db.createObjectStore("PoolDrainOptions");
        if (!db.objectStoreNames.contains("EpmdData")) {
          const epmdDataStore = db.createObjectStore("EpmdData", {
            keyPath: "id"
          });
          epmdDataStore.createIndex(
            "shipId,epmdType,wasteType,dateGroupBy,date",
            ["shipId", "epmdType", "wasteType", "dateGroupBy", "date"],
            {
              unique: true
            }
          );
        }
        if (!db.objectStoreNames.contains("EpmdMetaData")) {
          const epmdMetaStore = db.createObjectStore("EpmdMetaData", {
            keyPath: "id",
            autoIncrement: true
          });
          epmdMetaStore.createIndex(
            "shipId,epmdType,wasteType,dateGroupBy",
            ["shipId", "epmdType", "wasteType", "dateGroupBy"],
            { unique: true }
          );
        }
        if (!db.objectStoreNames.contains("MscaListData")) {
          const mscaMetaStore = db.createObjectStore("MscaListData", {
            keyPath: "id"
          });
          mscaMetaStore.createIndex("shipId", "shipId", {
            unique: false,
            multiEntry: true
          });
        }
        if (!db.objectStoreNames.contains("MetaData")) db.createObjectStore("MetaData");
        if (!db.objectStoreNames.contains("ActionsQueue")) {
          db.createObjectStore("ActionsQueue", {
            keyPath: "id",
            autoIncrement: true
          });
        }
        if (!db.objectStoreNames.contains("Ports")) {
          const portStore = db.createObjectStore("Ports", {
            keyPath: "id",
            autoIncrement: false
          });
          portStore.createIndex("name", "name", { unique: false });
        }
        if (!db.objectStoreNames.contains("MaritimeStandards")) {
          db.createObjectStore("MaritimeStandards");
        }
        if (!db.objectStoreNames.contains("MscaReports")) {
          db.createObjectStore("MscaReports");
        }
      }
    });
  }

  private async clearDb() {
    await (await this.$db).clear("EpmdHeaders");
    await (await this.$db).clear("Companies");
    await (await this.$db).clear("Valid");
    await (await this.$db).clear("EpmdAverages");
    await (await this.$db).clear("Ships");
    await (await this.$db).clear("PoolDrainOptions");
    await (await this.$db).clear("EpmdData");
    await (await this.$db).clear("EpmdMetaData");
    await (await this.$db).clear("MaritimeStandards");
    await (await this.$db).clear("MscaListData");
    await (await this.$db).clear("MscaReports");
    // await (await this.$db).clear("MetaData");
  }

  private async setOfflineDate(date: Date) {
    return (await this.$db).put("MetaData", getUnixTime(date).toString(), 0);
  }

  private async getOfflineDate() {
    const data = await (await this.$db).get("MetaData", 0);
    if (data == undefined) {
      const currentDate = new Date();
      await this.setOfflineDate(currentDate);
      return currentDate;
    }
    return fromUnixTime(parseInt(data));
  }

  private showErrorExceptionMessage(error: any) {
    if (!error) return;
    const toastMessage: IMessage = { color: "error", message: "" };
    console.error(error);

    if (error.response) {
      /*
       * The request was made and the server responded with a
       * status code that falls out of the range of 2xx
       */
      console.error(error.response.data);
      if (error.response.status === 401) {
        toastMessage.message = `Login Failed. ${error.response.statusText}`;
      } else {
        console.info(error.response);
        if (!error.response.title && error.response.data) {
          const msg = error.response.data as string;
          const indexOf = msg.indexOf("\n");
          if (indexOf > -1) {
            toastMessage.message = msg.substr(0, indexOf);
            this.state.toastMessage.push(toastMessage);
            return;
          }
        }
        toastMessage.message = error.response.data?.title ?? error.response.data;
      }
    } else if (error.request) {
      /*
       * The request was made but no response was received, `error.request`
       * is an instance of XMLHttpRequest in the browser and an instance
       * of http.ClientRequest in Node.js
       */
      toastMessage.message = "The request was made but no response was received";
    } else {
      // Something happened in setting up the request and triggered an Error
      toastMessage.message = error.message;
    }
    this.state.toastMessage.push(toastMessage);
  }

  public static encryptData<T>(data: T) {
    const key = UserAuthService.getEncryptKey();
    if (!key) throw new Error("Key out of sync");
    return btoa(JSON.stringify(sjcl.encrypt(key, JSON.stringify(data))));
  }

  public static decryptData<T>(encryptedData: string): T {
    const key = UserAuthService.getEncryptKey();
    if (!key) throw new Error("Key out of sync");
    return JSON.parse(sjcl.decrypt(key, JSON.parse(atob(encryptedData)))) as T;
  }
}
